import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import {
    AltitudeValueDto,
    DateFormatService,
    DisplayNotice,
    FEATURE_MISSION_CALENDAR,
    LocationService,
    MissionDetailsDto,
    MissionSummary,
    NoticesService,
    SimplePersonDto
} from '@flyfreely-portal-ui/flyfreely';
import { LotamDialoguesService } from '@flyfreely-portal-ui/notices';
import { FormatMissionStatusIconPipe } from '@flyfreely-portal-ui/ui';
import {
    CurrentOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { CalendarEvent, CalendarView } from 'angular-calendar';
import * as moment from 'moment-timezone';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import { findRpic, getMonthRange } from '../helpers';

const colors: { [colour: string]: { primary: string; secondary: string } } = {
    transparent: {
        primary: 'transparent',
        secondary: 'transparent'
    },
    red: {
        primary: '#ad2121',
        secondary: '#FAE3E3'
    },
    blue: {
        primary: '#1e90ff',
        secondary: '#D1E8FF'
    },
    yellow: {
        primary: '#e3bc08',
        secondary: '#FDF1BA'
    },
    grey: {
        primary: '#d5d5dc',
        secondary: '#4A4A4A'
    },
    green: {
        primary: '#42b983',
        secondary: '#00de7a'
    }
};

interface MissionCalendarEvent extends CalendarEvent {
    eventType: 'mission';
    status: MissionDetailsDto.Status;
    time: string;
    locationName: string;
    name: string;
    rpaNames: string[];
    rpic: SimplePersonDto;
    isDummy: boolean;
}

interface LotamCalendarEvent extends CalendarEvent {
    eventType: 'lotam';
    lotamId: number;
    noticeTypeColour: string | null | undefined;
    truncatedSubject: string;
    upperLimit: AltitudeValueDto;
    lowerLimit: AltitudeValueDto;
}

@Component({
    selector: 'mission-calendar-dialogue',
    templateUrl: './mission-calendar.component.html',
    styles: [
        `
            .cal-event-container span {
                word-break: break-word;
            }
            :host ::ng-deep .cal-day-cell {
                min-height: 150px;
            }
        `
    ]
})
export class MissionCalendarDialogue implements OnInit, OnDestroy {
    @Input() missions$: Observable<MissionSummary[]>;
    @Output() showMission = new EventEmitter<MissionSummary>();
    @Output() updateMissions = new EventEmitter<{
        startTime: string;
        endTime: string;
    }>();

    view: CalendarView = CalendarView.Month;

    CalendarView = CalendarView;

    viewDate: Date = new Date();

    refresh = new Subject<void>();

    events$: Observable<(MissionCalendarEvent | LotamCalendarEvent)[]>;

    lotamsByDay: Map<string, LotamCalendarEvent[]> = new Map();
    missionsByDay: Map<string, MissionCalendarEvent[]> = new Map();

    activeDayIsOpen = true;

    missionCalendarFeature = [FEATURE_MISSION_CALENDAR];

    showNotices = true;

    currentOrganisationId: number;

    private showNoticesSubject = new BehaviorSubject<boolean>(false);
    private monthRangeSubject: BehaviorSubject<{
        start: string;
        end: string;
    }>;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private modal: BsModalRef<MissionCalendarDialogue>,
        private locationService: LocationService,
        private missionStatusIconPipe: FormatMissionStatusIconPipe,
        private dateFormatService: DateFormatService,
        private noticesService: NoticesService,
        private workspaceStateService: WorkspaceStateService,
        private lotamDialoguesService: LotamDialoguesService
    ) {
        const { startOfMonth, endOfMonth } = getMonthRange(
            new Date().toISOString()
        );
        this.monthRangeSubject = new BehaviorSubject({
            start: startOfMonth,
            end: endOfMonth
        });
    }

    ngOnInit() {
        this.workspaceStateService.currentOrganisation$
            .pipe(
                filter(o => o.type === 'organisation_loaded'),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(o => {
                this.currentOrganisationId = (
                    o as CurrentOrganisation
                ).organisation.id;
            });

        const missionEvents$ = this.missions$.pipe(
            map(missions => missions.map(m => this.toMissionCalendarEvent(m))),
            tap(missions => this.buildMissionDayMap(missions))
        );

        const lotamEvents$ = combineLatest([
            this.showNoticesSubject,
            this.monthRangeSubject,
            this.workspaceStateService.currentOrganisation$.pipe(
                filter(o => o.type === 'organisation_loaded')
            )
        ]).pipe(
            switchMap(([showNotices, monthRange, org]) => {
                if (!showNotices) return of([]);
                const organisationId = (org as CurrentOrganisation).organisation
                    .id;
                return this.noticesService
                    .findNotices(
                        {
                            organisationId,
                            time: {
                                start: monthRange.start,
                                end: monthRange.end
                            },
                            audience: 'FLIGHT_OPERATIONS'
                        },
                        { page: 0, pageSize: 1000, sortFields: [] }
                    )
                    .pipe(
                        map(result =>
                            result.results.map(n =>
                                this.toLotamCalendarEvent(n)
                            )
                        ),
                        catchError(() => of([]))
                    );
            }),
            tap(lotams => this.buildLotamDayMap(lotams))
        );

        this.events$ = combineLatest([missionEvents$, lotamEvents$]).pipe(
            map(([missions, lotams]) => [...missions, ...lotams])
        );

        this.showNoticesSubject.next(this.showNotices);
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    toggleNotices() {
        this.showNoticesSubject.next(this.showNotices);
    }

    protected getDayKey(date: Date): string {
        return moment(date).format('YYYY-MM-DD');
    }

    missionColour(mission: MissionSummary) {
        const col = this.missionStatusIconPipe.findStatusColour(mission.status);
        switch (col) {
            case 'green':
                return colors.green;

            case 'yellow':
                return colors.yellow;

            case 'grey':
                return colors.grey;

            case 'red':
                return colors.red;

            case 'blue':
                return colors.blue;

            default:
                return colors.transparent;
        }
    }

    calculateMissionTime(start: string | Date, duration: number) {
        const timeFormat = this.dateFormatService.getTimeFormat();
        const startTime = moment(start).format(timeFormat);
        const endTime = moment(start)
            .add(duration, 'seconds')
            .format(timeFormat);
        return `${startTime} - ${endTime}`;
    }

    findLocationName(mission: MissionSummary) {
        if (mission.location != null) {
            return mission.location.name;
        } else {
            return `Mission: ${mission.name}`;
        }
    }

    /* setView(view: any) {
        this.view = view;
        this.refresh.next();
    } */

    openEvent(event: CalendarEvent) {
        this.missions$.subscribe(missions => {
            const mission = missions.find(m => m.id === event.id);
            this.showMission.emit(mission);
        });
    }

    openLotamEvent(event: LotamCalendarEvent) {
        this.lotamDialoguesService.showLotamDetailDialogue(
            event.lotamId,
            this.currentOrganisationId
        );
    }

    /* openWeek(date: any) {
        this.viewDate = date;
        this.view = CalendarView.Week;
    } */

    closeOpenMonthViewDay() {
        this.refreshMissions();
        this.activeDayIsOpen = false;
        const { startOfMonth, endOfMonth } = getMonthRange(
            this.viewDate.toISOString()
        );
        this.monthRangeSubject.next({
            start: startOfMonth,
            end: endOfMonth
        });
    }

    refreshMissions() {
        const { startOfMonth, endOfMonth } = getMonthRange(
            this.viewDate.toISOString()
        );

        this.updateMissions.next({
            startTime: startOfMonth,
            endTime: endOfMonth
        });
    }

    hide() {
        this.modal.hide();
    }

    private buildLotamDayMap(events: LotamCalendarEvent[]): void {
        const map = new Map<string, LotamCalendarEvent[]>();
        for (const event of events) {
            const current = moment(event.start).startOf('day');
            const dayEnd = event.end
                ? moment(event.end).startOf('day')
                : moment(event.start).startOf('day');
            while (current.isSameOrBefore(dayEnd, 'day')) {
                const key = current.format('YYYY-MM-DD');
                if (!map.has(key)) map.set(key, []);
                map.get(key)!.push(event);
                current.add(1, 'day');
            }
        }
        this.lotamsByDay = map;
    }

    private buildMissionDayMap(events: MissionCalendarEvent[]): void {
        const map = new Map<string, MissionCalendarEvent[]>();
        for (const event of events) {
            const current = moment(event.start).startOf('day');
            const dayEnd = event.end
                ? moment(event.end).startOf('day')
                : moment(event.start).startOf('day');
            while (current.isSameOrBefore(dayEnd, 'day')) {
                const key = current.format('YYYY-MM-DD');
                if (!map.has(key)) map.set(key, []);
                map.get(key)!.push(event);
                current.add(1, 'day');
            }
        }
        this.missionsByDay = map;
    }

    private toMissionCalendarEvent(
        mission: MissionSummary
    ): MissionCalendarEvent {
        return {
            eventType: 'mission',
            start: moment(mission.missionDate).toDate(),
            id: mission.id,
            color: this.missionColour(mission),
            title: this.findLocationName(mission),
            end: moment(mission.missionDate)
                .add(mission.missionEstimatedTime, 'seconds')
                .toDate(),
            draggable: false,
            status: mission.status,
            time: this.calculateMissionTime(
                mission.missionDate,
                mission.missionEstimatedTime
            ),
            locationName: this.findLocationName(mission),
            rpaNames: mission.rpaLinkList.map(r => r.rpa.nickname),
            name: this.findLocationName(mission),
            rpic: findRpic(mission.missionCrew),
            isDummy: mission.isDummy
        };
    }

    private toLotamCalendarEvent(notice: DisplayNotice): LotamCalendarEvent {
        const maxLen = 25;
        const truncated =
            notice.subject?.length > maxLen
                ? notice.subject.substring(0, maxLen) + '…'
                : notice.subject ?? '';
        return {
            eventType: 'lotam',
            lotamId: notice.id,
            start: moment(notice.startTime).toDate(),
            end: notice.endTime ? moment(notice.endTime).toDate() : undefined,
            title: notice.subject,
            draggable: false,
            noticeTypeColour: notice.noticeType?.colour,
            truncatedSubject: truncated,
            upperLimit: notice.upperLimit,
            lowerLimit: notice.lowerLimit
        };
    }
}
